<template lang="pug">
.returns-card(v-if="returns.length")
    .returns-card__header
        h4.returns-card__title
            i.fas.el-icon-fa-rotate-left
            | {{ $t('Returns') }}
            span.returns-card__count {{ returns.length }}

    .return-block(
        v-for="cancellation in returns"
        :key="`return-${cancellation.id}`"
    )
        //- Head: status badge + title + inline submission date. Everything
        //- left-aligned so the eye doesn't jump to right-floating meta.
        .return-block__head
            span.status-badge(:class="`status-${cancellation.status}`")
                template(v-if="cancellation.status === 'pending'") {{ $t('Pending') }}
                template(v-else-if="cancellation.status === 'approved'") {{ $t('Approved') }}
                template(v-else-if="cancellation.status === 'rejected'")
                    template(v-if="cancellation.rejection_reason === 'expired'") {{ $t('Expired') }}
                    template(v-else) {{ $t('Rejected') }}

            span.return-block__title(v-if="cancellation.items && cancellation.items.length")
                | {{ $tc('returnRequestItems', itemQty(cancellation), { count: itemQty(cancellation) }) }}
                span.return-block__title-sep ·
                span.return-block__date {{ cancellation.created_at | timestamp2date(dateTimeFormat) }}
            span.return-block__title(v-else)
                | {{ $t('Return submitted') }}
                span.return-block__title-sep ·
                span.return-block__date {{ cancellation.created_at | timestamp2date(dateTimeFormat) }}

            //- Inline amount keeps the admin's eye on the money. The amount
            //- on the failed badge is the ATTEMPT, not what got transferred —
            //- helps the admin reason about gateway refusals (e.g. seeing
            //- 22.000,00 € here is a fat-finger / unit-mismatch tell).
            .return-block__refund(v-if="cancellation.refund_status === 'succeeded' || cancellation.refund_status === 'succeeded-manual'")
                i.el-icon-fa-check.fas
                |  {{ $t('Refunded') }}: {{ cancellation.credited_amount | moneyFormat(currency) }}
            .return-block__refund.refund-failed(v-else-if="cancellation.refund_status === 'failed'")
                i.el-icon-fa-exclamation-triangle.fas
                |  {{ $t('Refund failed') }}: {{ cancellation.credited_amount | moneyFormat(currency) }}

        //- What the shop found at goods-in. Read-only in every case: the admin
        //- path captures it in the reject drawer, the expired path is set by
        //- AutoRejectStaleReturns without admin input. Absent on rejections
        //- that predate the cause, which are never back-filled.
        .return-block__cause(v-if="causeLabelFor(cancellation)")
            i.el-icon-fa-circle-info.fas
            span.return-block__cause-label {{ $t('Cause of rejection') }}:
            | {{ causeLabelFor(cancellation) }}

        //- Customer's overall comment on the return (free-text from the
        //- "Allgemeiner Kommentar" textarea in the storefront submission
        //- form). Separate from the per-item pivot.comment shown under
        //- each line — this one applies to the whole return.
        .return-block__comment(v-if="cancellation.comment")
            span.return-block__comment-label {{ $t('Comment') }}:
            | {{ cancellation.comment }}

        //- Items the customer wants to return. Shown for every multi-item
        //- return so the admin can scan what's coming back without
        //- opening a separate drawer. Each item carries qty + chosen
        //- reason + optional free-text comment.
        ul.return-block__items(v-if="cancellation.items && cancellation.items.length")
            li.return-block__item(
                v-for="item in cancellation.items"
                :key="`return-item-${cancellation.id}-${item.id}`"
            )
                .return-block__item-line
                    span.return-block__item-name {{ item.name || item.sku }}
                    small.return-block__item-sku(v-if="item.name && item.sku") {{ item.sku }}
                    span.return-block__item-qty × {{ itemPivot(item).qty || 1 }}
                    span.return-block__item-reason(v-if="reasonNameFor(item)")
                        span.return-block__item-sep ·
                        | {{ $t('Reason') }}: {{ reasonNameFor(item) }}
                small.return-block__item-comment(v-if="commentFor(item)")
                    i.el-icon-fa-comment.far
                    | {{ commentFor(item) }}

        //- One block per dispatched email scope. Strip ALWAYS stacks
        //- under the label so the row reads top-to-bottom: what was
        //- sent → when it was sent → engagement state.
        .return-block__email(
            v-for="email in scopedEmails(cancellation)"
            :key="`${cancellation.id}-${email.scope}`"
        )
            .return-block__email-meta
                span.return-block__email-label {{ email.label }}
                small.return-block__email-when(v-if="email.sentEmail || email.fallbackSentAt")
                    | · {{ $t('sent at') }} {{ formatTimestamp(email.sentEmail ? email.sentEmail.created_at : email.fallbackSentAt) }}
            EngagementStrip(
                :sent-email="email.sentEmail"
                :fallback-sent-at="email.fallbackSentAt"
                :date-time-format="dateTimeFormat"
            )

        //- All row-level actions live here: decision (Approve / Reject)
        //- when pending, receipt (Mark as received) regardless of decision,
        //- manual-refund recording for non-auto-refundable payments, and
        //- per-scope Resend buttons.
        .return-block__actions(v-if="hasAnyActions(cancellation)")
            //- Approve — single button regardless of payment method. The
            //- drawer hosts a "Refund automatically" checkbox for auto-
            //- refundable gateways (mirrors the original cancellation
            //- drawer pattern); manual payments get a hint instead.
            el-button(
                v-if="cancellation.status === 'pending'"
                size="mini"
                type="primary"
                plain
                icon="el-icon-fa-check fas"
                @click="openApprove(cancellation)"
            ) {{ $t('Approve…') }}
            el-button(
                v-if="cancellation.status === 'pending'"
                size="mini"
                type="danger"
                plain
                icon="el-icon-fa-xmark fas"
                @click="openReject(cancellation)"
            ) {{ $t('Reject…') }}
            //- Mark as received — opens the per-item review drawer
            el-button(
                v-if="canMarkReceived(cancellation)"
                size="mini"
                plain
                icon="el-icon-fa-box-archive fas"
                @click="openReceived(cancellation)"
            ) {{ $t('Mark as received…') }}
            //- "Mark refund issued" was removed once "Approve (refund
            //- manually)" became single-step (it now records the manual
            //- settlement together with the approval, so a separate later
            //- step is redundant). The CO_MarkRefundIssued mutation stays
            //- on the backend in case a shop later wants a deferred
            //- bookkeeping flow.
            el-button(
                v-for="email in scopedEmails(cancellation).filter(e => canResend(e.scope, cancellation))"
                :key="`resend-${cancellation.id}-${email.scope}`"
                size="mini"
                icon="el-icon-fa-rotate fas"
                @click="$emit(emitNameFor(email.scope), cancellation)"
            ) {{ $t(resendLabelFor(email.scope)) }}

    //- Drawers + dialog. They live outside the v-for so the same instance is
    //- reused regardless of which cancellation triggered the open.
    MarkReceivedDrawer(
        :visibility.sync="drawerState.markReceived.visible"
        :cancellation="drawerState.markReceived.cancellation"
        :stock-options="stockOptions"
        @success="onActionSuccess"
    )
    ApproveRefundDrawer(
        :visibility.sync="drawerState.approve.visible"
        :cancellation="drawerState.approve.cancellation"
        :auto-refundable="autoRefundable"
        :currency="currency"
        @success="onActionSuccess"
    )
    RejectReturnDrawer(
        :visibility.sync="drawerState.reject.visible"
        :cancellation="drawerState.reject.cancellation"
        @success="onActionSuccess"
    )
    //- MarkRefundIssuedDialog left mounted but never opened from the UI — see
    //- the comment in the actions block. Removed once we confirm no shop
    //- needs the deferred bookkeeping flow.
</template>

<i18n>
{
    "en": {
        "returnRequestItems": "Return request: 1 item | Return request: {count} items",
        "Approve…": "Approve…",
        "Reject…": "Reject…",
        "Mark as received…": "Mark as received…",
        "returned-wrong-item": "Wrong item returned",
        "exchange-requested": "Exchange requested instead of withdrawal",
        "item-missing-in-parcel": "Item was not in the parcel",
        "third-party-item": "Third-party item (not from our range)",
        "hygiene-seal-broken": "Hygiene seal broken",
        "item-damaged-by-customer": "Item returned damaged",
        "return-period-exceeded": "Withdrawal period exceeded"
    },
    "de": {
        "Returns": "Widerrufe",
        "sent at": "versendet am",
        "Pending": "Ausstehend",
        "Approved": "Genehmigt",
        "Rejected": "Abgelehnt",
        "Expired": "Abgelaufen",
        "Refunded": "Erstattet",
        "Refund failed": "Erstattung fehlgeschlagen",
        "Return submitted": "Widerruf eingereicht",
        "Acknowledgment of receipt": "Eingangsbestätigung",
        "Status notification": "Statusbenachrichtigung",
        "Reminder": "Erinnerung",
        "Resend reminder": "Erinnerung erneut versenden",
        "Approve & refund": "Genehmigen & erstatten",
        "Reject": "Ablehnen",
        "Resend": "Erneut versenden",
        "Resend acknowledgment of receipt": "Eingangsbestätigung erneut versenden",
        "Resend status notification": "Statusbenachrichtigung erneut versenden",
        "Reason": "Grund",
        "Cause of rejection": "Grund der Ablehnung",
        "Comment": "Kommentar",
        "Approve…": "Genehmigen …",
        "Reject…": "Ablehnen …",
        "Mark as received…": "Wareneingang erfassen …",
        "returned-wrong-item": "Falscher Artikel zurückgesendet",
        "exchange-requested": "Umtausch statt Widerruf",
        "item-missing-in-parcel": "Artikel lag dem Paket nicht bei",
        "third-party-item": "Fremdartikel (nicht aus unserem Sortiment)",
        "hygiene-seal-broken": "Hygienesiegel beschädigt",
        "item-damaged-by-customer": "Artikel beschädigt zurückgesendet",
        "return-period-exceeded": "Widerrufsfrist überschritten",
        "returnRequestItems": "Widerruf-Anfrage: 1 Artikel | Widerruf-Anfrage: {count} Artikel"
    }
}
</i18n>

<script>
import _ from 'lodash';
import { timestamp2date, moneyFormat } from '@/filters';
import EngagementStrip from '@/extensions/shop/views/Purchase/Form/Partials/EngagementStrip.vue';
import MarkReceivedDrawer from '@/extensions/shop/views/Purchase/Form/Partials/MarkReceivedDrawer.vue';
import ApproveRefundDrawer from '@/extensions/shop/views/Purchase/Form/Partials/ApproveRefundDrawer.vue';
import RejectReturnDrawer from '@/extensions/shop/views/Purchase/Form/Partials/RejectReturnDrawer.vue';
import { REJECTION_CAUSES } from '@/extensions/shop/rejectionCauses';

const SCOPES = [
    { scope: 'return-received', sentAtKey: 'return_received_sent_at', label: 'Acknowledgment of receipt', icon: 'el-icon-fa-envelope-circle-check' },
    { scope: 'return-status-changed', sentAtKey: 'return_status_changed_sent_at', label: 'Status notification', icon: 'el-icon-fa-bullhorn' },
    { scope: 'return-reminder', sentAtKey: 'return_reminder_sent_at', label: 'Reminder', icon: 'el-icon-fa-bell' },
];

export default {
    name: 'Returns',
    components: {
        EngagementStrip,
        MarkReceivedDrawer,
        ApproveRefundDrawer,
        RejectReturnDrawer,
    },
    filters: { timestamp2date, moneyFormat },
    props: {
        cancellations: {
            type: Array,
            default: () => [],
        },
        purchaserEmail: {
            type: String,
            default: '',
        },
        dateTimeFormat: {
            type: String,
            required: true,
        },
        autoRefundable: {
            type: Boolean,
            default: false,
        },
        stockOptions: {
            type: Array,
            default: () => [],
        },
        currency: {
            type: String,
            default: '€',
        },
    },
    data: () => ({
        drawerState: {
            markReceived: { visible: false, cancellation: null },
            approve: { visible: false, cancellation: null },
            reject: { visible: false, cancellation: null },
        },
    }),
    computed: {
        returns() {
            return _.filter(this.cancellations, c => c.type === 'return');
        },
        hasPurchaserEmail() {
            return !!(this.purchaserEmail && this.purchaserEmail.length);
        },
    },
    methods: {
        itemQty(cancellation) {
            if (!cancellation.items) return 0;
            return cancellation.items.reduce((acc, item) => acc + _.get(item, 'pivot.qty', 0), 0);
        },
        itemPivot(item) {
            return _.get(item, 'pivot') || {};
        },
        reasonNameFor(item) {
            return _.get(item, 'pivot.reason.name', '');
        },
        commentFor(item) {
            return _.get(item, 'pivot.comment', '');
        },
        scopedEmails(cancellation) {
            const sentEmails = cancellation.sentEmails || [];
            // Render in chronological order (oldest first) instead of the
            // static SCOPES order. Otherwise a status-notification sent two
            // days after the reminder still rendered above it, which is
            // misleading. Prefer the matched SentEmail.created_at when
            // available, otherwise fall back to the per-scope sent_at
            // accessor on the cancellation row.
            return _.map(SCOPES, scopeMeta => {
                const fallbackSentAt = _.get(cancellation, scopeMeta.sentAtKey);
                if (!fallbackSentAt) return null;
                const matched = _.findLast(sentEmails, se => se.type === scopeMeta.scope) || null;
                return {
                    ...scopeMeta,
                    label: this.$t(scopeMeta.label),
                    sentEmail: matched,
                    fallbackSentAt,
                };
            })
                .filter(Boolean)
                .sort((a, b) => {
                    const aSent = _.get(a, 'sentEmail.created_at') || a.fallbackSentAt || 0;
                    const bSent = _.get(b, 'sentEmail.created_at') || b.fallbackSentAt || 0;
                    return aSent - bSent;
                });
        },
        formatTimestamp(value) {
            return value ? timestamp2date(value, this.dateTimeFormat) : '';
        },
        canResend(scope, cancellation) {
            if (!this.hasPurchaserEmail) return false;
            if (scope === 'return-received') return !!cancellation.return_received_sent_at;
            if (scope === 'return-status-changed') return !!cancellation.return_status_changed_sent_at;
            return false;
        },
        emitNameFor(scope) {
            if (scope === 'return-received') return 'resend-received';
            if (scope === 'return-status-changed') return 'resend-status-changed';
            return 'resend';
        },
        resendLabelFor(scope) {
            if (scope === 'return-received') return 'Resend acknowledgment of receipt';
            if (scope === 'return-status-changed') return 'Resend status notification';
            return 'Resend';
        },
        canMarkReceived(cancellation) {
            return !cancellation.received_at;
        },
        hasAnyActions(cancellation) {
            if (cancellation.status === 'pending') return true;
            if (this.canMarkReceived(cancellation)) return true;
            return this.scopedEmails(cancellation).some(e => this.canResend(e.scope, cancellation));
        },
        openReceived(cancellation) {
            this.drawerState.markReceived = {
                visible: true,
                cancellation,
            };
        },
        openApprove(cancellation) {
            this.drawerState.approve = {
                visible: true,
                cancellation,
            };
        },
        openReject(cancellation) {
            this.drawerState.reject = {
                visible: true,
                cancellation,
            };
        },
        // Historic rejections predate the cause and carry none — they must
        // render without a label rather than an untranslated key.
        causeLabelFor(cancellation) {
            const cause = _.get(cancellation, 'rejection_cause');
            if (!cause || !REJECTION_CAUSES.includes(cause)) return '';
            return this.$t(cause);
        },
        onActionSuccess() {
            // Bubble up so the parent (Main.vue) refreshes the purchase. The
            // drawers themselves already issued the GraphQL mutation; we just
            // need the form state to pick up the new cancellation data.
            this.$emit('refresh');
        },
    },
};
</script>

<style lang="sass" scoped>
.returns-card
    background: var(--background-base, #fafafa)
    border: 1px solid var(--border-color, #ebeef5)
    border-radius: 6px
    padding: 12px 16px

.returns-card__header
    display: flex
    align-items: center
    padding-bottom: 8px
    margin-bottom: 8px
    border-bottom: 1px solid var(--border-color, #ebeef5)

.returns-card__title
    margin: 0
    display: flex
    align-items: center
    gap: 8px
    font-size: 14px
    font-weight: 600

.returns-card__count
    background: rgba(0, 0, 0, 0.06)
    color: var(--color-text-secondary, #606266)
    border-radius: 10px
    padding: 1px 8px
    font-size: 11px
    font-weight: 500

.return-block
    display: flex
    flex-direction: column
    gap: 12px

// Only add a divider BETWEEN return-blocks. The header already has a
// border-bottom that separates it from the first block, so we use the
// adjacent-sibling combinator instead of `:first-of-type` (which would
// pick the .returns-card__header div).
.return-block + .return-block
    border-top: 1px solid var(--border-color, #ebeef5)
    padding-top: 12px
    margin-top: 12px

// Head row — status badge + label + (small) date + (optional) refund pill.
.return-block__head
    display: flex
    align-items: center
    flex-wrap: wrap
    gap: 8px

.return-block__title
    font-size: 13px
    color: var(--color-text-regular, #303133)
    display: inline-flex
    align-items: baseline
    gap: 4px

.return-block__title-sep
    color: var(--color-text-muted, #909399)

.return-block__date
    color: var(--color-text-secondary, #606266)
    font-size: 12px
    white-space: nowrap

.return-block__refund
    font-size: 11px
    color: $--color-success
    margin-left: auto
    white-space: nowrap
    i
        margin-right: 4px
    &.refund-failed
        color: $--color-warning

.status-badge
    display: inline-block
    padding: 1px 8px
    border-radius: 10px
    font-size: 11px
    font-weight: 600
    line-height: 1.4
    text-transform: uppercase
    letter-spacing: 0.02em
    background: rgba(0, 0, 0, 0.06)
    color: $--color-text-secondary
    &.status-pending
        background: rgba(255, 152, 0, 0.15)
        color: #b26a00
    &.status-approved
        background: rgba(76, 175, 80, 0.15)
        color: #2e7d32
    &.status-rejected
        background: rgba(244, 67, 54, 0.15)
        color: #c62828

// Items being returned — compact list, one line per item with optional
// comment beneath. Keeps multi-item returns readable without expanding
// into a separate drawer.
.return-block__items
    list-style: none
    margin: 0
    padding: 0
    display: flex
    flex-direction: column
    gap: 6px

.return-block__item
    font-size: 13px
    color: var(--color-text-regular, #303133)
    display: flex
    flex-direction: column
    gap: 2px

.return-block__item-line
    display: flex
    align-items: baseline
    flex-wrap: wrap
    gap: 6px

.return-block__item-name
    font-weight: 500

.return-block__item-sku
    color: var(--color-text-muted, #909399)
    font-size: 11px

.return-block__item-qty
    color: var(--color-text-secondary, #606266)
    font-size: 12px
    white-space: nowrap

.return-block__item-reason
    color: var(--color-text-secondary, #606266)
    font-size: 12px

.return-block__item-sep
    color: var(--color-text-muted, #909399)
    margin-right: 4px

.return-block__item-comment
    color: var(--color-text-muted, #909399)
    font-size: 12px
    font-style: italic
    padding-left: 16px
    display: flex
    align-items: baseline
    gap: 6px
    i
        font-size: 11px

.return-block__cause
    color: var(--color-text-regular, #303133)
    font-size: 13px
    margin-bottom: 8px
    i
        font-size: 11px
        margin-right: 6px
        color: $--color-danger

.return-block__cause-label
    color: var(--color-text-secondary, #606266)
    font-weight: 500
    margin-right: 4px

.return-block__comment
    color: var(--color-text-regular, #303133)
    font-size: 13px
    font-style: italic
    margin-bottom: 8px

.return-block__comment-label
    color: var(--color-text-secondary, #606266)
    font-weight: 500
    font-style: normal
    margin-right: 4px

// Email block — vertical stack: meta line, then strip below.
.return-block__email
    display: flex
    flex-direction: column
    gap: 8px

.return-block__email-meta
    display: flex
    align-items: baseline
    flex-wrap: wrap
    gap: 4px
    font-size: 13px
    color: var(--color-text-regular, #303133)

.return-block__email-label
    font-size: 13px

.return-block__email-when
    color: var(--color-text-secondary, #606266)
    font-size: 12px
    white-space: nowrap

.return-block__actions
    display: flex
    flex-wrap: wrap
    gap: 8px
</style>
